import { type MaybeRef, ref, type Ref, watch } from "vue";
import { useToast } from "vue-toastification";

import {
  type BarcodeScan,
  type SerialScanEvent,
  useBarcodeScanning,
} from "@/common/composables/useBarcodeScanning";
import { connectWebSocketsAdminHub } from "@/common/composables/useWebSockets";
import { getErrorMessage } from "@/common/utils/getErrorMessage";
import type { ChipScanEvent, SignalREvent } from "@/events/types";
import { getSerialScanData } from "@/products/api/queries";
import { useCurrentUser } from "@/users/public/composables";

export const useOrderPageScanning = () => {
  const toast = useToast();
  const chipScannedEvent = useRealTimeScanning();

  const scannedOrderId = ref<string | undefined>(undefined);

  watch(
    chipScannedEvent,
    () => {
      if (chipScannedEvent.value?.orderId === null) {
        toast.warning("No order", { timeout: 5000 });

        return;
      }

      scannedOrderId.value = chipScannedEvent.value?.orderId;
    },
    { immediate: false },
  );

  return scannedOrderId;
};

type Params = {
  /**
   * Whether the real-time scanning should be active or not.
   * If not provided, it will be active by default.
   */
  isActive?: MaybeRef<boolean>;
};

export const useRealTimeScanning = (params?: Params) => {
  const toast = useToast();
  const chipScannedEvent: Ref<ChipScanEvent | SerialScanEvent | null> =
    ref(null);

  const { user } = useCurrentUser();
  try {
    connectWebSocketsAdminHub(
      "all",
      (ev: SignalREvent) => {
        if (ev.type !== "ChipRequestedByUid") return;

        const signalEvent = ev.payload;

        if (!signalEvent) return;

        // always ignore events when the window/tab is not focused
        if (!document.hasFocus()) {
          return;
        }

        // don't listen to events that are not triggered by the current admin user
        if (user.value?.id !== signalEvent?.userId) return;

        chipScannedEvent.value = signalEvent;
      },
      (error) => {
        // eslint-disable-next-line no-console
        console.error("Error connecting to real-time updates", error);
        toast.error(
          `Error connecting to real-time updates: ${getErrorMessage(error)[0]}`,
        );
      },
      params?.isActive,
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }

  const scannedBarcode = useBarcodeScanning();
  watch(scannedBarcode, async (newBarcodeScan) => {
    if (!newBarcodeScan?.barcodeContent) return;

    chipScannedEvent.value = await getScanDataBySerial(newBarcodeScan);
  });

  return chipScannedEvent;
};

const getScanDataBySerial = async (
  barcodeScan: BarcodeScan,
): Promise<SerialScanEvent> => {
  const response = await getSerialScanData(barcodeScan.barcodeContent!);

  return {
    chipId: response.chipId ?? "",
    orderId: response.orderId ?? "",
    externalReferenceId: response.productListingId ?? "",
    barcodeType: barcodeScan.barcodeType!,
  };
};
